import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  ViewModule as ComponentIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { PageDefinition, CreatePageRequest, PageSection, PageComponent } from '../../types/pages';
import { v4 as uuidv4 } from 'uuid';
import ComponentPalette from './ComponentPalette';
import SectionComponentManager from './SectionComponentManager';

interface PageBuilderProps {
  page?: PageDefinition | null;
  onSave: (pageData: CreatePageRequest) => void;
  onCancel: () => void;
}

const PageBuilder: React.FC<PageBuilderProps> = ({ page, onSave, onCancel }) => {
  const isEditing = Boolean(page);
  const [sections, setSections] = useState<PageSection[]>([]);
  const [componentPaletteOpen, setComponentPaletteOpen] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  
  const { control, handleSubmit, formState: { errors } } = useForm<CreatePageRequest>({
    defaultValues: {
      name: page?.name || '',
      title: page?.title || '',
      description: page?.description || '',
      route: page?.route || '/',
      category: page?.category || 'Other',
      layout: page?.layout || {
        type: 'single-column',
        sections: []
      },
      permissions: page?.permissions || {
        view: ['admin', 'manager']
      },
      meta: page?.meta || {},
      styling: page?.styling || {}
    }
  });

  useEffect(() => {
    if (page?.layout?.sections) {
      setSections(page.layout.sections);
    } else {
      // Initialize with one default section
      setSections([{
        id: uuidv4(),
        order: 0,
        width: 'full',
        components: []
      }]);
    }
  }, [page]);

  // const layoutType = watch('layout.type');

  const addSection = () => {
    const newSection: PageSection = {
      id: uuidv4(),
      order: (sections || []).length,
      width: 'full',
      components: []
    };
    setSections([...(sections || []), newSection]);
  };

  const removeSection = (sectionId: string) => {
    setSections((sections || []).filter(s => s.id !== sectionId));
  };

  const updateSection = (sectionId: string, updates: Partial<PageSection>) => {
    setSections((sections || []).map(s => 
      s.id === sectionId ? { ...s, ...updates } : s
    ));
  };

  const addComponentToSection = (sectionId: string, component: PageComponent) => {
    setSections((sections || []).map(s => 
      s.id === sectionId ? {
        ...s,
        components: [...(s.components || []), { ...component, order: (s.components || []).length }]
      } : s
    ));
  };

  const updateSectionComponents = (sectionId: string, components: PageComponent[]) => {
    setSections((sections || []).map(s => 
      s.id === sectionId ? { ...s, components } : s
    ));
  };

  const handleComponentPaletteAdd = (component: PageComponent) => {
    if (selectedSectionId) {
      addComponentToSection(selectedSectionId, component);
      setComponentPaletteOpen(false);
      setSelectedSectionId(null);
    }
  };

  const openComponentPalette = (sectionId: string) => {
    setSelectedSectionId(sectionId);
    setComponentPaletteOpen(true);
  };

  const onSubmit = (data: CreatePageRequest) => {
    const pageData = {
      ...data,
      layout: {
        ...data.layout,
        sections: sections
      }
    };
    onSave(pageData);
  };

  const categoryOptions = [
    'Dashboard',
    'Client Management',
    'Loan Processing',
    'Reporting',
    'Configuration',
    'Other'
  ];

  const layoutTypeOptions = [
    { value: 'single-column', label: 'Single Column' },
    { value: 'two-column', label: 'Two Column' },
    { value: 'three-column', label: 'Three Column' },
    { value: 'grid', label: 'Grid' },
    { value: 'custom', label: 'Custom' }
  ];

  const widthOptions = [
    { value: 'full', label: 'Full Width' },
    { value: 'half', label: 'Half Width' },
    { value: 'third', label: 'One Third' },
    { value: 'quarter', label: 'One Quarter' },
    { value: 'two-thirds', label: 'Two Thirds' },
    { value: 'three-quarters', label: 'Three Quarters' }
  ];

  const roleOptions = ['admin', 'manager', 'analyst', 'viewer'];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={onCancel}
          sx={{ mr: 2 }}
        >
          Back to Pages
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          {isEditing ? `Edit Page: ${page?.name}` : 'Create New Page'}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: 'Page name is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Page Name"
                        fullWidth
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="title"
                    control={control}
                    rules={{ required: 'Page title is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Page Title"
                        fullWidth
                        error={!!errors.title}
                        helperText={errors.title?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid size={12}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        fullWidth
                        multiline
                        rows={2}
                      />
                    )}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="route"
                    control={control}
                    rules={{ 
                      required: 'Route is required',
                      pattern: {
                        value: /^\/[a-z0-9-/]*$/,
                        message: 'Route must start with / and contain only lowercase letters, numbers, hyphens, and slashes'
                      }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Route"
                        fullWidth
                        error={!!errors.route}
                        helperText={errors.route?.message || 'e.g., /custom-page'}
                      />
                    )}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="category"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Category</InputLabel>
                        <Select {...field} label="Category">
                          {categoryOptions.map((category) => (
                            <MenuItem key={category} value={category}>
                              {category}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Layout Configuration */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Layout Configuration</Typography>
              
              <Controller
                name="layout.type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Layout Type</InputLabel>
                    <Select {...field} label="Layout Type">
                      {layoutTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              {/* Sections */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">Sections</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addSection}
                  >
                    Add Section
                  </Button>
                </Box>

                {(sections || []).map((section, index) => (
                  <Accordion key={section.id} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Typography>
                          Section {index + 1} - {section.name || 'Untitled'}
                        </Typography>
                        <Chip 
                          size="small" 
                          label={`${section.components?.length || 0} components`}
                          color={section.components?.length ? 'primary' : 'default'}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Section Name"
                            value={section.name || ''}
                            onChange={(e) => updateSection(section.id, { name: e.target.value })}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Width</InputLabel>
                            <Select
                              value={section.width}
                              onChange={(e) => updateSection(section.id, { width: e.target.value as PageSection['width'] })}
                              label="Width"
                            >
                              {widthOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        {/* Section Components Management */}
                        <Grid size={12}>
                          <SectionComponentManager
                            components={section.components || []}
                            onComponentsChange={(components) => updateSectionComponents(section.id, components)}
                            onAddComponent={() => openComponentPalette(section.id)}
                          />
                        </Grid>
                        
                        <Grid size={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => removeSection(section.id)}
                              disabled={(sections || []).length === 1}
                            >
                              Remove Section
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Sidebar Configuration */}
          <Grid size={{ xs: 12, md: 4 }}>
            {/* Permissions */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Permissions</Typography>
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Who can view this page?</Typography>
              <Controller
                name="permissions.view"
                control={control}
                render={({ field }) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {roleOptions.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        clickable
                        color={field.value.includes(role) ? 'primary' : 'default'}
                        onClick={() => {
                          const newRoles = field.value.includes(role)
                            ? field.value.filter(r => r !== role)
                            : [...field.value, role];
                          field.onChange(newRoles);
                        }}
                      />
                    ))}
                  </Box>
                )}
              />
            </Paper>

            {/* SEO Meta */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>SEO & Meta</Typography>
              
              <Controller
                name="meta.pageTitle"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="HTML Page Title"
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                  />
                )}
              />
              
              <Controller
                name="meta.pageDescription"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Meta Description"
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                  />
                )}
              />
            </Paper>

            {/* Actions */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Actions</Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  fullWidth
                >
                  {isEditing ? 'Update Page' : 'Create Page'}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  fullWidth
                >
                  Cancel
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </form>

      {/* Component Palette Dialog */}
      <Dialog 
        open={componentPaletteOpen} 
        onClose={() => setComponentPaletteOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ComponentIcon />
          Add Component to Section
        </DialogTitle>
        <DialogContent>
          <ComponentPalette onAddComponent={handleComponentPaletteAdd} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComponentPaletteOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PageBuilder;